import React, { useState } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip,
  Avatar, Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VideoPreviewPlayer from './VideoPreviewPlayer';

const AVATAR_COLORS = ['#4F46E5','#7C3AED','#0D9488','#059669','#DC2626','#D97706','#2563EB'];
const getAvatarBg = (email = '') => {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = email.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const formatFileSize = (bytes) => {
  if (!bytes) return '—';
  const k = 1024, sizes = ['B','KB','MB','GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};

const formatTimeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 3600000);
  if (diff < 1) return 'Just now';
  if (diff < 24) return `${diff}h ago`;
  return `${Math.floor(diff / 24)}d ago`;
};

const truncate = (str = '', n) => str.length <= n ? str : str.slice(0, n) + '…';

// ── Single job row (desktop) ────────────────────────────────────────────────
const JobRow = ({ job, onApprove, onReject, onViewDetails, onVideoPreview, processing }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '160px 1fr 1fr 110px 90px auto',
    gap: 2,
    alignItems: 'center',
    px: 3, py: 2.5,
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' },
  }}>
    {/* Video preview */}
    <Box sx={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: '#000', aspectRatio: '16/9' }}>
      <VideoPreviewPlayer
        videoUrl={job.video_url}
        filename={job.video_filename}
        onFullscreenView={() => onVideoPreview && onVideoPreview(job)}
      />
    </Box>

    {/* User */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      <Avatar sx={{ width: 32, height: 32, backgroundColor: getAvatarBg(job.user_email), fontSize: '13px', flexShrink: 0 }}>
        {(job.user_email || '?')[0].toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {truncate(job.user_email, 28)}
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {truncate(job.user_id, 20)}
        </Typography>
      </Box>
    </Box>

    {/* Video */}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 500, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>
        {truncate(job.video_filename, 32)}
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
        {formatFileSize(job.video_file_size)}{job.num_speakers > 0 ? ` · ${job.num_speakers} speakers` : ''}
      </Typography>
    </Box>

    {/* Language */}
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.4, borderRadius: '5px',
      backgroundColor: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      <LanguageIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }} />
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>
        {job.source_language} → {job.target_language}
      </Typography>
    </Box>

    {/* Time */}
    <Box>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
        {formatTimeAgo(job.created_at)}
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: 'rgba(255,255,255,0.2)', mt: 0.25 }}>
        {new Date(job.created_at).toLocaleDateString()}
      </Typography>
    </Box>

    {/* Actions */}
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
      <Tooltip title="Approve">
        <Button
          size="small"
          onClick={() => onApprove(job)}
          disabled={processing}
          startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          sx={{
            backgroundColor: 'rgba(34,197,94,0.12)', color: '#22C55E',
            border: '1px solid rgba(34,197,94,0.25)',
            fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '12px',
            textTransform: 'none', borderRadius: '6px', px: 1.5, py: 0.6,
            '&:hover': { backgroundColor: 'rgba(34,197,94,0.2)' },
            '&:disabled': { opacity: 0.4 },
          }}
        >
          Approve
        </Button>
      </Tooltip>

      <Tooltip title="Reject">
        <Button
          size="small"
          onClick={() => onReject(job)}
          disabled={processing}
          startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
          sx={{
            backgroundColor: 'rgba(239,68,68,0.1)', color: '#EF4444',
            border: '1px solid rgba(239,68,68,0.2)',
            fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '12px',
            textTransform: 'none', borderRadius: '6px', px: 1.5, py: 0.6,
            '&:hover': { backgroundColor: 'rgba(239,68,68,0.18)' },
            '&:disabled': { opacity: 0.4 },
          }}
        >
          Reject
        </Button>
      </Tooltip>

      <Tooltip title="View details">
        <IconButton
          size="small"
          onClick={() => onViewDetails && onViewDetails(job)}
          sx={{
            color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '6px', p: 0.75,
            '&:hover': { color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.05)' },
          }}
        >
          <VisibilityIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
);

// ── Mobile card ──────────────────────────────────────────────────────────────
const MobileJobCard = ({ job, onApprove, onReject, onViewDetails, onVideoPreview, processing }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box sx={{
      backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '12px', overflow: 'hidden', mb: 2,
    }}>
      <Box sx={{ borderRadius: '0', overflow: 'hidden', aspectRatio: '16/9', backgroundColor: '#000' }}>
        <VideoPreviewPlayer videoUrl={job.video_url} filename={job.video_filename} onFullscreenView={() => onVideoPreview?.(job)} />
      </Box>

      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Avatar sx={{ width: 30, height: 30, backgroundColor: getAvatarBg(job.user_email), fontSize: '12px', flexShrink: 0 }}>
            {(job.user_email || '?')[0].toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truncate(job.user_email, 30)}
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
              {formatTimeAgo(job.created_at)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setExpanded(v => !v)} sx={{ color: 'rgba(255,255,255,0.3)', p: 0.5 }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.5)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {truncate(job.video_filename, 30)}
          </Typography>
          <Box sx={{ px: 1, py: 0.3, borderRadius: '5px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>
              {job.source_language} → {job.target_language}
            </Typography>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mb: 1.5, p: 1.5, borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
            {[
              ['File size', formatFileSize(job.video_file_size)],
              ['Submitted', new Date(job.created_at).toLocaleDateString()],
              ...(job.num_speakers > 0 ? [['Speakers', job.num_speakers]] : []),
            ].map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, '&:last-child': { mb: 0 } }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{k}</Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{v}</Typography>
              </Box>
            ))}
          </Box>
        </Collapse>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button fullWidth onClick={() => onApprove(job)} disabled={processing} startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            sx={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.25)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', textTransform: 'none', borderRadius: '7px', py: 0.9, '&:hover': { backgroundColor: 'rgba(34,197,94,0.2)' }, '&:disabled': { opacity: 0.4 } }}>
            Approve
          </Button>
          <Button fullWidth onClick={() => onReject(job)} disabled={processing} startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
            sx={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', textTransform: 'none', borderRadius: '7px', py: 0.9, '&:hover': { backgroundColor: 'rgba(239,68,68,0.18)' }, '&:disabled': { opacity: 0.4 } }}>
            Reject
          </Button>
          <IconButton onClick={() => onViewDetails?.(job)}
            sx={{ color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '7px', px: 1.5, '&:hover': { color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.2)' } }}>
            <VisibilityIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const PendingJobsTable = ({ jobs, loading, onApprove, onReject, onViewDetails, onVideoPreview }) => {
  const [processingJobs, setProcessingJobs] = useState(new Set());

  const wrap = (fn, job) => async () => {
    if (processingJobs.has(job.id)) return;
    setProcessingJobs(p => new Set([...p, job.id]));
    try { await fn(job); } finally {
      setProcessingJobs(p => { const n = new Set(p); n.delete(job.id); return n; });
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 10, textAlign: 'center', backgroundColor: '#0A0A0A', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '14px' }}>Loading…</Typography>
      </Box>
    );
  }

  if (!jobs || jobs.length === 0) {
    return (
      <Box sx={{ py: 10, textAlign: 'center', backgroundColor: '#0A0A0A', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
        <CheckCircleIcon sx={{ fontSize: 40, color: '#22C55E', mb: 2, opacity: 0.7 }} />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '15px', color: '#FFFFFF', mb: 0.5 }}>
          All Caught Up
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>
          No jobs pending approval at the moment.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <Box sx={{
        display: { xs: 'none', md: 'block' },
        backgroundColor: '#0A0A0A',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        {/* Header row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 1fr 110px 90px auto',
          gap: 2, px: 3, py: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backgroundColor: 'rgba(255,255,255,0.02)',
        }}>
          {['Preview', 'User', 'Video', 'Languages', 'Submitted', 'Actions'].map(h => (
            <Typography key={h} sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {h}
            </Typography>
          ))}
        </Box>

        {jobs.map(job => (
          <JobRow
            key={job.id}
            job={job}
            onApprove={wrap(onApprove, job)}
            onReject={wrap(onReject, job)}
            onViewDetails={onViewDetails}
            onVideoPreview={onVideoPreview}
            processing={processingJobs.has(job.id)}
          />
        ))}
      </Box>

      {/* Mobile cards */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {jobs.map(job => (
          <MobileJobCard
            key={job.id}
            job={job}
            onApprove={wrap(onApprove, job)}
            onReject={wrap(onReject, job)}
            onViewDetails={onViewDetails}
            onVideoPreview={onVideoPreview}
            processing={processingJobs.has(job.id)}
          />
        ))}
      </Box>
    </>
  );
};

export default PendingJobsTable;
